import { useEffect, useState } from "react";
import { Plus, Trash2, Wallet } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { paymentsApi, docBalance } from "@/api";
import { useLang, fmtMoney, fmtDate } from "@/i18n";
import { PaymentDialog } from "@/components/PaymentDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";

export const PaymentsSection = ({ doc, onChange }) => {
  const { t, lang } = useLang();
  const [payments, setPayments] = useState([]);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const balance = docBalance(doc);

  useEffect(() => { paymentsApi.list(doc.id).then(setPayments); }, [doc.id, doc.paid_amount]);

  const remove = async () => {
    try {
      const res = await paymentsApi.remove(deleting.id);
      toast.success(t("payment_deleted"));
      onChange(res.document);
    } catch { toast.error(t("error")); } finally { setDeleting(null); }
  };

  return (
    <div className="erp-card no-print" data-testid="payments-section">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-2">
          <Wallet size={18} className="text-blue-700" />
          <h3 className="font-heading font-semibold">{t("payments")}</h3>
        </div>
        <div className="flex items-center gap-4 text-sm font-mono">
          <span className="text-slate-500">{t("paid_amount")}: <span className="text-emerald-700 dark:text-emerald-300" data-testid="document-paid-amount">{fmtMoney(doc.paid_amount)}</span></span>
          <span className="text-slate-500">{t("balance")}: <span className={balance > 0 ? "text-amber-700 dark:text-amber-300 font-semibold" : "text-slate-400"} data-testid="document-balance">{fmtMoney(balance)}</span></span>
          {balance > 0 && doc.status !== "cancelled" && (
            <Button size="sm" onClick={() => setOpen(true)} className="bg-blue-800 hover:bg-blue-700 font-body" data-testid="add-payment-button">
              <Plus size={14} className="mr-1" /> {t("add_payment")}
            </Button>
          )}
        </div>
      </div>
      {payments.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-slate-500" data-testid="payments-empty">{t("no_payments")}</div>
      ) : (
        <table className="w-full text-sm erp-table">
          <thead>
            <tr className="text-left">
              <th className="px-5 py-2">{t("date")}</th>
              <th className="px-5 py-2">{t("payment_method")}</th>
              <th className="px-5 py-2 hidden sm:table-cell">{t("reference")}</th>
              <th className="px-5 py-2 text-right">{t("amount")}</th>
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {payments.map((p) => (
              <tr key={p.id} data-testid={`payment-row-${p.id}`}>
                <td className="px-5 py-2.5 font-mono">{fmtDate(p.date, lang)}</td>
                <td className="px-5 py-2.5">{t(`pay_${p.method}`)}</td>
                <td className="px-5 py-2.5 hidden sm:table-cell text-slate-500">{p.reference || p.notes || "—"}</td>
                <td className="px-5 py-2.5 text-right font-mono font-medium text-emerald-700 dark:text-emerald-300">{fmtMoney(p.amount)}</td>
                <td className="px-2 py-2.5">
                  <button onClick={() => setDeleting(p)} data-testid={`payment-delete-${p.id}`} className="h-8 w-8 inline-flex items-center justify-center rounded text-rose-600 hover:bg-rose-100 dark:hover:bg-rose-950"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <PaymentDialog open={open} onOpenChange={setOpen} doc={doc} onSaved={onChange} />
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} message={t("confirm_delete_payment")} onConfirm={remove} />
    </div>
  );
};
